import { type KeyboardEvent, type SyntheticEvent, useCallback } from 'react'

import {
  Divider,
  IconImage,
  QuantityCounter,
  SecondaryButton,
  Space,
  SubText,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { useFormatPrice } from '@webapp/core/hooks/useFormatPrice'
import { useFormatStaticAsset } from '@webapp/core/hooks/useFormatStaticAsset'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { CatalogItemDeliveryLabel } from '../CatalogItemDeliveryLabel'
import { CatalogItemRating } from '../CatalogItemRating'
import { CatalogItemTag, type ICatalogItemTagProps } from '../CatalogItemTag'
import { VendorLogoBadge, VendorLogoBadgeMarkHeights } from '../VendorLogoBadge'
import { CatalogItemBodyStyled } from './styles/CatalogItemBodyStyled'
import { CatalogItemContentStyled } from './styles/CatalogItemContentStyled'
import { CatalogItemFulfillmentLogoStyled } from './styles/CatalogItemFulfillmentLogoStyled'
import { CatalogItemImageStyled } from './styles/CatalogItemImageStyled'
import { CatalogItemImageWrapperStyled } from './styles/CatalogItemImageWrapperStyled'
import { CatalogItemLayoutStyled } from './styles/CatalogItemLayoutStyled'
import { CatalogItemPriceStyled } from './styles/CatalogItemPriceStyled'
import { CatalogItemTitleStyled } from './styles/CatalogItemTitleStyled'
import {
  CatalogItemTagIcons,
  CatalogItemTagVariants,
  resolveInfoBadges,
} from '../../value-objects/CatalogItemTag'
import {
  type ConversationCatalogItemDelivery,
  type ConversationCatalogItemRating,
  type ConversationCatalogItemVendorBadge,
  type ConversationVendor,
  type ConversationVolumePricing,
} from '../../value-objects/ConversationTurnItem'
import { CatalogItemVendorBadgeSlots } from '../../value-objects/enums'

const SOURCE_TAG_ICONS = [
  CatalogItemTagIcons.CATALOG,
  CatalogItemTagIcons.PREVIOUS_ORDER,
] as const

const isSourceTag = (tag: ICatalogItemTagProps): boolean =>
  !!tag.icon && (SOURCE_TAG_ICONS as readonly string[]).includes(tag.icon)

export interface ICatalogItemProps {
  img?: string
  title: string
  body: string
  tags: ICatalogItemTagProps[]
  price: number
  currency: CurrencyName
  isSelected?: boolean
  vendor?: ConversationVendor
  rating?: ConversationCatalogItemRating
  vendorBadges?: ConversationCatalogItemVendorBadge[]
  delivery?: ConversationCatalogItemDelivery
  listPrice?: number
  volumePricing?: ConversationVolumePricing
  quantity: number
  isActionsDisabled?: boolean
  onClick: () => void
  onQuantityChange: (quantity: number) => void
  onAddToRequest: () => void
}

export const CatalogItem = ({
  img,
  title,
  body,
  tags,
  price,
  currency,
  isSelected = false,
  vendor,
  rating,
  vendorBadges = [],
  delivery,
  listPrice,
  volumePricing,
  quantity,
  isActionsDisabled = false,
  onClick,
  onQuantityChange,
  onAddToRequest,
}: ICatalogItemProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()
  const formatPrice = useFormatPrice()
  const formatStaticAsset = useFormatStaticAsset()

  const infoBadges = resolveInfoBadges(vendorBadges)
  const fulfillmentBadge = vendorBadges.find(
    (badge) =>
      badge.slot === CatalogItemVendorBadgeSlots.FULFILLMENT && badge.logoUrl
  )
  const dealBadge = vendorBadges.find(
    (badge) => badge.slot === CatalogItemVendorBadgeSlots.DEAL
  )
  const savingsHint = volumePricing?.savingsHint
  const hasDeliveryRow = Boolean(delivery || fulfillmentBadge)
  const sourceTags = tags.filter(isSourceTag)
  const policyTags = tags.filter((tag) => !isSourceTag(tag))
  const hasSourceRow = Boolean(vendor?.logoUrl || sourceTags.length > 0)

  const handleActionsActivation = useCallback((event: SyntheticEvent) => {
    event.stopPropagation()
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    },
    [onClick]
  )

  return (
    <CatalogItemLayoutStyled
      data-analytics-id='catalog-item-card'
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role='button'
      tabIndex={0}
      aria-pressed={isSelected}
      $isSelected={isSelected}
    >
      <Space direction='horizontal' size='small' align='start'>
        <CatalogItemImageWrapperStyled>
          {img ? (
            <CatalogItemImageStyled src={formatMedia(img)} alt='' />
          ) : (
            <IconImage size={24} color='darkGrey' aria-hidden />
          )}
        </CatalogItemImageWrapperStyled>
        <Space.Item grow={1} minWidth={0}>
          <CatalogItemContentStyled>
            <Space direction='vertical' size='medium'>
              <Space direction='vertical' size='xsmall'>
                {hasSourceRow && (
                  <Space
                    direction='horizontal'
                    size='small'
                    align='center'
                    flexWrap
                  >
                    {vendor?.logoUrl && (
                      <VendorLogoBadge
                        vendor={vendor}
                        markHeight={VendorLogoBadgeMarkHeights.DEFAULT}
                        outlined={false}
                      />
                    )}
                    {sourceTags.map((tag, index) => (
                      <CatalogItemTag
                        key={`${index}-${tag.text}`}
                        icon={tag.icon}
                        text={tag.text}
                        variant={tag.variant}
                      />
                    ))}
                  </Space>
                )}
                <CatalogItemTitleStyled className='fs-mask'>
                  {title}
                </CatalogItemTitleStyled>
                <CatalogItemBodyStyled className='fs-mask'>
                  {body}
                </CatalogItemBodyStyled>
              </Space>
              <Space direction='vertical' size='xsmall'>
                {dealBadge && (
                  <CatalogItemTag
                    text={dealBadge.text}
                    variant={CatalogItemTagVariants.DEAL}
                  />
                )}
                <Space
                  direction='horizontal'
                  size='small'
                  align='center'
                  flexWrap
                >
                  <CatalogItemPriceStyled className='fs-mask'>
                    <Price inline price={price} currency={currency} />
                  </CatalogItemPriceStyled>
                  {!!listPrice && (
                    <SubText
                      inline
                      noWrap
                      color='textSecondary'
                      className='fs-mask'
                    >
                      {intl.formatMessage(messages.listPriceLabel)}{' '}
                      <s>{formatPrice(listPrice)}</s>
                    </SubText>
                  )}
                  {savingsHint && (
                    <Space direction='horizontal' size='xsmall' align='center'>
                      <CatalogItemTag
                        text={intl.formatMessage(messages.saveBadge, {
                          pct: savingsHint.savingsPct,
                        })}
                        variant={CatalogItemTagVariants.MUTED}
                      />
                      <SubText noWrap color='textSecondary'>
                        {intl.formatMessage(messages.savingsHintOn, {
                          label: savingsHint.label,
                        })}
                      </SubText>
                    </Space>
                  )}
                </Space>
                {hasDeliveryRow && (
                  <Space direction='horizontal' size='small' align='center'>
                    {fulfillmentBadge?.logoUrl && (
                      <CatalogItemFulfillmentLogoStyled
                        src={formatStaticAsset(fulfillmentBadge.logoUrl)}
                        alt={fulfillmentBadge.text}
                      />
                    )}
                    {delivery && (
                      <CatalogItemDeliveryLabel delivery={delivery} />
                    )}
                  </Space>
                )}
              </Space>
              {(rating || infoBadges.length > 0) && (
                <Space
                  direction='horizontal'
                  size='small'
                  align='center'
                  flexWrap
                >
                  {rating && (
                    <CatalogItemRating
                      value={rating.value}
                      count={rating.count}
                      scaleMax={rating.scaleMax}
                    />
                  )}
                  {infoBadges.map((badge) => (
                    <CatalogItemTag
                      key={badge.slot}
                      icon={badge.icon}
                      text={badge.text}
                      logoUrl={badge.logoUrl}
                      variant={badge.variant}
                    />
                  ))}
                </Space>
              )}
            </Space>
          </CatalogItemContentStyled>
        </Space.Item>
      </Space>
      <Divider space={0} />
      <Space
        direction='horizontal'
        size='small'
        align='center'
        justify={policyTags.length > 0 ? 'space-between' : 'end'}
      >
        {policyTags.length > 0 && (
          <Space.Item grow={1} minWidth={0}>
            <Space direction='horizontal' size='small' align='center' flexWrap>
              {policyTags.map((tag, index) => (
                <CatalogItemTag
                  key={`${index}-${tag.text}`}
                  icon={tag.icon}
                  text={tag.text}
                  variant={tag.variant}
                />
              ))}
            </Space>
          </Space.Item>
        )}
        <Space.Item
          onClick={handleActionsActivation}
          onKeyDown={handleActionsActivation}
        >
          <Space direction='horizontal' size='medium' align='center'>
            <QuantityCounter
              isValueEditable
              value={quantity}
              disabled={isActionsDisabled}
              aria-label={intl.formatMessage(messages.quantityAriaLabel)}
              onChange={onQuantityChange}
            />
            <SecondaryButton
              analyticsId='catalog-item-add-to-request-button'
              disabled={isActionsDisabled}
              onClick={onAddToRequest}
            >
              {intl.formatMessage(messages.addToRequestButton)}
            </SecondaryButton>
          </Space>
        </Space.Item>
      </Space>
    </CatalogItemLayoutStyled>
  )
}
